import React, { useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import AdminSidebar from "./AdminSidebar";
import { toast } from "../../utils/toast";
import { adminFetch, adminErrorMessage } from "../../api/adminFetch";
import "../styles/AdminPostManager.css";

// 블로그 글 관리 — 작성 · 수정 · 삭제 · 공개 전환.
// 본문은 마크다운으로 쓰고 오른쪽에서 바로 결과를 본다.

const EMPTY = {
    title: "",
    slug: "",
    summary: "",
    content: "",
    coverImage: "",
    tags: [],
    published: false,
};

const AdminPostManager = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(true);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await adminFetch("/api/admin/posts");
            if (!res.ok) {
                setLoadError(await adminErrorMessage(res));
                setPosts([]);
                return;
            }
            setLoadError("");
            setPosts(await res.json());
        } catch (err) {
            console.error("글 목록 조회 실패", err);
            setLoadError("서버에 연결하지 못했습니다.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const startCreate = () => setEditing({ ...EMPTY });

    /** 목록 응답에는 본문이 빠져 있다. 수정할 때 한 건을 다시 받아 온다. */
    const startEdit = async (post) => {
        try {
            const res = await adminFetch(`/api/admin/posts/${post.id}`);
            if (!res.ok) { toast(await adminErrorMessage(res)); return; }
            const full = await res.json();
            setEditing({ ...EMPTY, ...full, tags: full.tags || [] });
        } catch (err) {
            console.error("글 조회 실패", err);
            toast("서버 오류가 발생했습니다.");
        }
    };

    const closeForm = () => setEditing(null);
    const setField = (name, value) => setEditing((prev) => ({ ...prev, [name]: value }));
    const onChange = (e) => setField(e.target.name, e.target.value);

    const save = async (publishNow) => {
        if (!editing.title.trim()) { toast("제목을 입력해 주세요."); return; }

        const payload = { ...editing };
        if (publishNow !== undefined) payload.published = publishNow;

        setSaving(true);
        try {
            const isNew = !editing.id;
            const res = await adminFetch(
                isNew ? "/api/admin/posts" : `/api/admin/posts/${editing.id}`,
                {
                    method: isNew ? "POST" : "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                }
            );
            if (!res.ok) { toast(await adminErrorMessage(res)); return; }

            const saved = await res.json();
            toast(payload.published ? "공개했습니다." : "임시 저장했습니다.");
            // 주소(slug)는 서버가 정하므로 저장 결과로 폼을 갱신한다.
            setEditing({ ...EMPTY, ...saved, tags: saved.tags || [] });
            load();
        } catch (err) {
            console.error("글 저장 실패", err);
            toast("서버 오류가 발생했습니다.");
        } finally {
            setSaving(false);
        }
    };

    const uploadImage = async (file, asCover) => {
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const res = await adminFetch("/api/admin/posts/image", { method: "POST", body: form });
            if (!res.ok) { toast(await adminErrorMessage(res)); return; }

            const { src } = await res.json();
            if (asCover) {
                setField("coverImage", src);
                toast("대표 이미지를 설정했습니다.");
            } else {
                // 본문 끝에 마크다운 이미지 문법으로 붙인다.
                setEditing((prev) => ({
                    ...prev,
                    content: `${prev.content || ""}\n\n![${file.name.replace(/\.[^.]+$/, "")}](${src})\n`,
                }));
                toast("본문에 이미지를 넣었습니다.");
            }
        } catch (err) {
            console.error("이미지 업로드 실패", err);
            toast("업로드에 실패했습니다.");
        } finally {
            setUploading(false);
        }
    };

    const remove = async (post) => {
        try {
            const res = await adminFetch(`/api/admin/posts/${post.id}`, { method: "DELETE" });
            if (!res.ok) { toast(await adminErrorMessage(res)); return; }
            toast("삭제했습니다.");
            setConfirmDelete(null);
            if (editing?.id === post.id) closeForm();
            load();
        } catch (err) {
            console.error("글 삭제 실패", err);
            toast("서버 오류가 발생했습니다.");
        }
    };

    const togglePublished = async (post) => {
        try {
            const res = await adminFetch(`/api/admin/posts/${post.id}`);
            if (!res.ok) { toast(await adminErrorMessage(res)); return; }
            const full = await res.json();

            const put = await adminFetch(`/api/admin/posts/${post.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...full, published: !full.published }),
            });
            if (!put.ok) { toast(await adminErrorMessage(put)); return; }
            load();
        } catch (err) {
            console.error("공개 설정 변경 실패", err);
            toast("서버 오류가 발생했습니다.");
        }
    };

    if (loading) {
        return (
            <div className="sch-adminPage">
                <AdminSidebar />
                <div className="sch-adminMain"><p className="sc-caption">불러오는 중…</p></div>
            </div>
        );
    }

    return (
        <div className="sch-adminPage">
            <AdminSidebar />

            <div className="sch-adminMain">
                <div className="sch-adminHead">
                    <h2 className="sc-h2">블로그 관리</h2>
                    <button type="button" className="sc-btn sc-btn--sm" onClick={startCreate}>새 글 쓰기</button>
                </div>

                {loadError && <div className="sc-alert sc-alert--danger">{loadError}</div>}

                {editing && (
                    <div className="sch-postform">
                        <div className="sch-adminHead">
                            <h3 className="sc-h3">{editing.id ? "글 수정" : "새 글"}</h3>
                            <label className="sc-choice">
                                <input type="checkbox" className="sc-check"
                                       checked={preview} onChange={(e) => setPreview(e.target.checked)} />
                                미리보기
                            </label>
                        </div>

                        <div className="sc-field">
                            <label className="sc-field__label" htmlFor="po-title">제목 *</label>
                            <input id="po-title" className="sc-input" name="title"
                                   value={editing.title} onChange={onChange} />
                        </div>

                        <div className="sch-postgrid">
                            <div className="sc-field">
                                <label className="sc-field__label" htmlFor="po-slug">주소(slug)</label>
                                <input id="po-slug" className="sc-input" name="slug"
                                       value={editing.slug} onChange={onChange} placeholder="excel-automation-guide" />
                                <span className="sc-field__help">
                                    영문·숫자·하이픈. 비우면 제목에서 자동으로 만듭니다.
                                    {editing.slug && <> → /blog/{editing.slug}</>}
                                </span>
                            </div>
                            <div className="sc-field">
                                <label className="sc-field__label" htmlFor="po-tags">태그</label>
                                <input id="po-tags" className="sc-input"
                                       value={(editing.tags || []).join(", ")}
                                       onChange={(e) => setField("tags",
                                           e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
                                <span className="sc-field__help">쉼표로 구분. 예) 엑셀, 크롤링</span>
                            </div>
                        </div>

                        <div className="sc-field">
                            <label className="sc-field__label" htmlFor="po-summary">요약</label>
                            <input id="po-summary" className="sc-input" name="summary"
                                   value={editing.summary} onChange={onChange} />
                            <span className="sc-field__help">
                                목록과 검색 결과에 나옵니다. 비우면 본문 앞부분을 씁니다.
                            </span>
                        </div>

                        <div className="sc-field">
                            <span className="sc-field__label">대표 이미지</span>
                            <div className="sch-postupload">
                                <label className="sc-btn sc-btn--secondary sc-btn--sm">
                                    {uploading ? "올리는 중…" : "대표 이미지"}
                                    <input type="file" accept="image/*" hidden disabled={uploading}
                                           onChange={(e) => { uploadImage(e.target.files?.[0], true); e.target.value = ""; }} />
                                </label>
                                <label className="sc-btn sc-btn--secondary sc-btn--sm">
                                    본문에 이미지 넣기
                                    <input type="file" accept="image/*" hidden disabled={uploading}
                                           onChange={(e) => { uploadImage(e.target.files?.[0], false); e.target.value = ""; }} />
                                </label>
                                {editing.coverImage && (
                                    <>
                                        <img className="sch-postcover" src={editing.coverImage} alt="" />
                                        <button type="button" className="sc-btn sc-btn--ghost sc-btn--sm"
                                                onClick={() => setField("coverImage", "")}>빼기</button>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className={`sch-posteditor${preview ? " sch-posteditor--split" : ""}`}>
                            <div className="sc-field">
                                <label className="sc-field__label" htmlFor="po-content">본문 (마크다운)</label>
                                <textarea id="po-content" className="sc-textarea sch-postarea" name="content"
                                          value={editing.content} onChange={onChange}
                                          placeholder={"## 소제목\n\n문단을 씁니다.\n\n- 목록\n- 목록\n\n**굵게** 와 [링크](https://example.com) 를 쓸 수 있습니다."} />
                                <span className="sc-field__help">
                                    ## 소제목 · **굵게** · [링크](주소) · - 목록 · ``` 코드 블록
                                </span>
                            </div>

                            {preview && (
                                <div className="sch-postpreview">
                                    <span className="sc-field__label">미리보기</span>
                                    <div className="sch-blogbody">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{editing.content || "_내용을 입력하면 여기에 보입니다._"}</ReactMarkdown>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="sch-postbtns">
                            <button type="button" className="sc-btn" disabled={saving}
                                    onClick={() => save(true)}>
                                {saving ? "저장 중…" : "공개"}
                            </button>
                            <button type="button" className="sc-btn sc-btn--secondary" disabled={saving}
                                    onClick={() => save(false)}>
                                임시 저장
                            </button>
                            <button type="button" className="sc-btn sc-btn--ghost" onClick={closeForm}>닫기</button>
                            {editing.slug && editing.published && (
                                <a className="sch-inlinelink" href={`/blog/${editing.slug}`}
                                   target="_blank" rel="noopener noreferrer">공개된 글 보기 ↗</a>
                            )}
                        </div>
                    </div>
                )}

                {posts.length === 0 && !loadError ? (
                    <div className="sc-empty">
                        <div className="sc-empty__title">아직 쓴 글이 없습니다</div>
                        <div className="sc-empty__desc">「새 글 쓰기」로 시작하세요.</div>
                    </div>
                ) : (
                    <div className="sc-tablewrap">
                        <table className="sc-table">
                            <thead>
                            <tr><th>제목</th><th>주소</th><th>태그</th><th>공개</th><th>관리</th></tr>
                            </thead>
                            <tbody>
                            {posts.map((p) => (
                                <tr key={p.id}>
                                    <td className="sch-postname">
                                        {p.title}
                                        {p.summary && <span>{p.summary}</span>}
                                    </td>
                                    <td className="sch-mono">/blog/{p.slug}</td>
                                    <td>{(p.tags || []).join(", ") || "—"}</td>
                                    <td>
                                        <button type="button"
                                                className={`sc-badge ${p.published ? "sc-badge--paid" : "sc-badge--done"}`}
                                                onClick={() => togglePublished(p)}
                                                title="눌러서 공개 여부를 바꿉니다">
                                            {p.published ? "공개" : "임시"}
                                        </button>
                                    </td>
                                    <td>
                                        <div className="sch-postrowbtns">
                                            <button type="button" className="sc-btn sc-btn--secondary sc-btn--sm"
                                                    onClick={() => startEdit(p)}>수정</button>
                                            <button type="button" className="sc-btn sc-btn--danger sc-btn--sm"
                                                    onClick={() => setConfirmDelete(p)}>삭제</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {confirmDelete && (
                <div className="sc-modal-backdrop" onClick={() => setConfirmDelete(null)}>
                    <div className="sc-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="sc-modal__head">
                            <span className="sc-modal__title">글을 삭제할까요?</span>
                            <button type="button" className="sc-modal__x"
                                    onClick={() => setConfirmDelete(null)} aria-label="닫기">✕</button>
                        </div>
                        <div className="sc-modal__body">
                            <p>「{confirmDelete.title}」가 지워집니다. 되돌릴 수 없습니다.</p>
                        </div>
                        <div className="sc-modal__foot">
                            <button type="button" className="sc-btn sc-btn--secondary"
                                    onClick={() => setConfirmDelete(null)}>취소</button>
                            <button type="button" className="sc-btn sc-btn--danger"
                                    onClick={() => remove(confirmDelete)}>삭제</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPostManager;
